/**
 * @file     navigation.ts
 * @purpose  Site header configuration: which navigation categories sit on the desktop rail, and where the Find homes action is hidden.
 */
import type { NavigationCategory } from '@/core/constants/navigation';

/** Desktop rail groups, in order. Categories left out (About) stay in the mobile sheet and the footer. */
export const NAV_RAIL_CATEGORY_KEYS: readonly NavigationCategory['key'][] = [
  'buy',
  'rent',
  'sell',
  'data',
  'tools',
  'learn',
];

/** The map search already is the home finder, so the header action would open a second one. */
const HOME_FINDER_HIDDEN_PREFIXES = ['/properties'] as const;

/**
 * The home hero is the finder, so the header action would repeat it. Exact match only.
 * '/index' is what the server sees when ISR revalidates the static home page, while the
 * browser reads '/'; both must agree or the header mismatches and React throws #418.
 */
const HOME_FINDER_HIDDEN_PATHS = ['/', '/index'] as const;

export const isHomeFinderActionHidden = (pathname: string | null | undefined): boolean =>
  Boolean(
    pathname &&
      ((HOME_FINDER_HIDDEN_PATHS as readonly string[]).includes(pathname) ||
        HOME_FINDER_HIDDEN_PREFIXES.some((prefix) => pathname.startsWith(prefix))),
  );

const stripQuery = (path: string) => path.split('?')[0];

const matchesPath = (pathname: string, path: string) => {
  const base = stripQuery(path);
  return pathname === base || pathname.startsWith(`${base}/`);
};

/**
 * The category the current URL belongs to. A category's own destination wins
 * over a child link, so a link shared by two categories (the mortgage
 * calculator) never moves the indicator away from the section it lives under.
 */
export const resolveActiveCategoryKey = (
  categories: readonly NavigationCategory[],
  pathname: string | null | undefined,
): NavigationCategory['key'] | null => {
  if (!pathname) return null;
  // Middleware serves signed-out listing pages from /properties-static, which
  // is what the server render sees; the browser sees /properties.
  pathname = pathname.replace(/^\/properties-static(?=\/|$)/, '/properties');
  const direct = categories.find((c) => c.directLink && matchesPath(pathname, c.directLink));
  if (direct) return direct.key;
  const child = categories.find((c) => c.items.some((item) => matchesPath(pathname, item.path)));
  return child?.key ?? null;
};
